import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import Loading from '../Loading';
import Error from '../Error';
import Results from './Results';
import QuestionPanel from './QuestionPanel';
import Conversation from './Conversation';
import ExerciseLayout from '../shared/ExerciseLayout';
import {
  fetchConversationExercise,
  evaluateConversationWritingExercise,
  recordExerciseAttempt,
  saveExerciseProgress,
  loadExerciseProgress,
  resetExerciseProgress
} from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';

const ConversationWriteExercise = forwardRef(({
  topic = "groceries",
  dialogueNumber = 1,
  onBack,
  // Poslechový režim znovupoužívá tuto komponentu: jiný exercise_type pro
  // evidenci pokroku a skrytý text dialogu do dokončení otázek.
  exerciseType = 'conversation_writing',
  listenMode = false,
}, ref) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [conversation, setConversation] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [evaluations, setEvaluations] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [engagementData, setEngagementData] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(false);

  const { stop: stopTimer } = useExerciseTimer();

  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      if (conversationId == null) return;
      try {
        await saveExerciseProgress(topic, exerciseType, {
          currentQuestionIndex,
          userAnswers,
          evaluations,
          showResults
        }, conversationId);
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  const fetchExercises = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchConversationExercise({ topic, dialogueNumber });

      setConversation(data.dialogue);
      setQuestions(data.questions);
      setConversationId(data.conversationId);

    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [topic, dialogueNumber]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  useEffect(() => {
    const loadProgress = async () => {
      // Wait until both the exercise content AND its conversation_id are known,
      // otherwise we'd hit the topic-scoped slot and load another dialogue's state.
      if (questions.length > 0 && conversationId != null) {
        try {
          const progressData = await loadExerciseProgress(topic, exerciseType, conversationId);

          if (progressData.progress_data) {
            const { currentQuestionIndex: savedIndex, userAnswers: savedAnswers, evaluations: savedEvaluations, showResults: savedShowResults } = progressData.progress_data;

            if (savedIndex) setCurrentQuestionIndex(savedIndex);
            if (savedAnswers) setUserAnswers(savedAnswers);
            if (savedEvaluations) setEvaluations(savedEvaluations);
            if (savedShowResults) setShowResults(true);
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [questions, topic, conversationId, exerciseType]);

  const handleAnswerChange = (e) => {
    setUserAnswers({
      ...userAnswers,
      [currentQuestionIndex]: e.target.value
    });
  };

  const getConversationContext = useCallback(() => {
    return conversation
      .map((line, idx) => `${idx + 1}. ${line.role}: ${line.text}`)
      .join('\n');
  }, [conversation]);

  const submitAnswer = async () => {
    if (isEvaluating) return;
    const currentQuestion = questions.find(q => q.id === currentQuestionIndex);
    const userAnswer = userAnswers[currentQuestionIndex] || '';

    try {
      setIsEvaluating(true);

      const result = await evaluateConversationWritingExercise(
        getConversationContext(),
        currentQuestion,
        userAnswer
      );

      setEvaluations(prev => ({
        ...prev,
        [currentQuestionIndex]: result
      }));

      try {
        await saveExerciseProgress(topic, exerciseType, {
          currentQuestionIndex,
          userAnswers,
          evaluations: {
            ...evaluations,
            [currentQuestionIndex]: result
          },
          showResults
        }, conversationId);
      } catch (err) {
        console.error('Error saving progress:', err);
      }

    } catch (err) {
      console.error('Error evaluating answer:', err);
      setError(err.message);
    } finally {
      setIsEvaluating(false);
    }
  };

  const next = async () => {
    if (currentQuestionIndex < questions.length) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      try {
        await saveExerciseProgress(topic, exerciseType, {
          currentQuestionIndex: nextIndex,
          userAnswers,
          evaluations,
          showResults
        }, conversationId);
      } catch (err) {
        console.error('Error saving progress:', err);
      }
    }
  };

  const back = () => {
    if (currentQuestionIndex > 1) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const finishExercise = async () => {
    if (isFinishing) return;
    setIsFinishing(true);
    try {
      const finalTime = stopTimer();

      const evaluationsArray = questions.map(q => {
        const evaluation = evaluations[q.id];
        return evaluation || {
          question_id: q.id,
          evaluation: 'Neodpovezeno',
          is_correct: false
        };
      });

      const correctCount = evaluationsArray.filter(e => e.is_correct).length;
      const totalCount = questions.length;

      const errors = evaluationsArray
        .filter(e => !e.is_correct)
        .map(e => ({
          exercise_id: e.question_id,
          details: e.evaluation
        }));

      // Show results immediately
      setEvaluations(evaluationsArray);
      setShowResults(true);
      setIsFinishing(false);

      // Fire recordExerciseAttempt in the background (fast - no LLM)
      setEngagementLoading(true);
      recordExerciseAttempt({
        topic: topic,
        exercise_type: exerciseType,
        conversation_id: conversationId,
        score: correctCount,
        max_score: totalCount,
        time_spent_seconds: finalTime,
        errors: errors.length > 0 ? errors : null,
        // Real ConversationQA IDs (q.qa_id), NOT the 1-based q.id
        // (question_number) used for navigation/evaluation — see api.js.
        exercise_ids: questions.map(q => q.qa_id)
      })
        .then(result => {
          setEngagementData(result);
          setEngagementLoading(false);
        })
        .catch(trackingError => {
          console.error('Failed to record exercise attempt:', trackingError);
          setEngagementLoading(false);
        });

      try {
        await saveExerciseProgress(topic, exerciseType, {
          currentQuestionIndex,
          userAnswers,
          evaluations: evaluationsArray,
          showResults: true
        }, conversationId);
      } catch (err) {
        console.error('Error saving progress on finish:', err);
      }
    } catch (err) {
      console.error('Error finishing exercise:', err);
      setError(err.message);
      setIsFinishing(false);
    }
  };

  const restart = async () => {
    setUserAnswers({});
    setCurrentQuestionIndex(1);
    setShowResults(false);
    setEvaluations({});
    setEngagementData(null);
    setEngagementLoading(false);

    try {
      await resetExerciseProgress(topic, exerciseType, conversationId);
    } catch (err) {
      console.error('Error resetting progress:', err);
    }

    await fetchExercises();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  if (showResults) {
    const evaluationsArray = Array.isArray(evaluations)
      ? evaluations
      : questions.map(q => evaluations[q.id] || {
          question_id: q.id,
          evaluation: 'Neodpovezeno',
          is_correct: false
        });

    return (
      <>
        {/* V poslechovém režimu se text dialogu odhalí až tady, po dokončení otázek. */}
        {listenMode && (
          <div className="max-w-3xl mx-auto px-4 pt-6">
            <Conversation conversation={conversation} topic={topic} title="Dialog — přepis" />
          </div>
        )}
        <Results
          questions={questions}
          userAnswers={userAnswers}
          evaluations={evaluationsArray}
          restart={restart}
          onBack={handleBack}
          topic={topic}
          engagementData={engagementData}
          engagementLoading={engagementLoading}
        />
      </>
    );
  }

  return (
    <ExerciseLayout
      title={listenMode ? "Poslech - Dialogy" : "Konverzace - Dialogy"}
      subtitle={listenMode ? "Nejdřív poslouchej, pak odpověz" : "Odpovídejte na otázky k dialogu"}
      color={listenMode ? "cyan" : "blue"}
      icon={listenMode ? "🎧" : "💬"}
      progress={{ current: currentQuestionIndex, total: questions.length }}
      onBack={handleBack}
      twoColumn={true}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dialog - leva strana */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">{listenMode ? "Poslech" : "Dialog"}</h5>
          <Conversation
            conversation={conversation}
            topic={topic}
            hideText={listenMode}
            title={listenMode ? "Poslechni si dialog" : "Přečtěte si konverzaci"}
          />
        </div>

        {/* Otázky - pravá strana */}
        <div>
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">Otázky</h5>
          <QuestionPanel
            currentQuestionIndex={currentQuestionIndex}
            questions={questions}
            userAnswers={userAnswers}
            handleAnswerChange={handleAnswerChange}
            back={back}
            next={next}
            submitAnswer={submitAnswer}
            evaluation={evaluations[currentQuestionIndex]}
            isEvaluating={isEvaluating}
            isLastQuestion={currentQuestionIndex === questions.length}
            onFinish={finishExercise}
            topic={topic}
          />
        </div>
      </div>
    </ExerciseLayout>
  );
});

export default ConversationWriteExercise;
